package com.example.positionahead.domain

import com.example.positionahead.model.AssetClass
import com.example.positionahead.model.LotSnapshot
import com.example.positionahead.model.PortfolioSummary
import com.example.positionahead.model.Position
import com.example.positionahead.model.PositionSnapshot
import com.example.positionahead.model.Quote
import com.example.positionahead.model.totalCostBasis
import com.example.positionahead.model.totalShares

object PortfolioCalculator {

    fun buildSummary(
        positions: List<Position>,
        quotes: Map<String, Quote>,
    ): PortfolioSummary {
        if (positions.isEmpty()) {
            return PortfolioSummary(
                totalMarketValue = 0.0,
                totalCostBasis = 0.0,
                totalUnrealizedGainLoss = 0.0,
                totalUnrealizedGainLossPercent = 0.0,
                estimatedAnnualIncome = 0.0,
                stockAllocationPercent = 0.0,
                bondAllocationPercent = 0.0,
                cashAllocationPercent = 0.0,
                positions = emptyList(),
            )
        }

        val snapshots = positions.map { position ->
            buildPositionSnapshot(position, quotes[position.symbol.uppercase()])
        }

        val pricedSnapshots = snapshots.filter { (it.quote?.price ?: 0.0) > 0.0 }
        val totalMarketValue = pricedSnapshots.sumOf { it.marketValue }
            .takeIf { pricedSnapshots.isNotEmpty() }
            ?: snapshots.sumOf { it.costBasis }
        val totalCostBasis = snapshots.sumOf { it.costBasis }
        val totalUnrealized = pricedSnapshots.sumOf { it.unrealizedGainLoss }
        val pricedCostBasis = pricedSnapshots.sumOf { it.costBasis }
        val totalUnrealizedPercent =
            if (pricedCostBasis > 0) (totalUnrealized / pricedCostBasis) * 100 else 0.0
        val totalIncome = snapshots.sumOf { it.estimatedAnnualIncome }

        val weightedSnapshots = snapshots.map { snapshot ->
            val weight = if (totalMarketValue > 0) {
                (snapshot.marketValue / totalMarketValue) * 100
            } else 0.0
            snapshot.copy(portfolioWeightPercent = weight)
        }

        var stockValue = 0.0
        var bondValue = 0.0
        var cashValue = 0.0
        weightedSnapshots.forEach { snapshot ->
            when (snapshot.position.assetClass) {
                AssetClass.BOND -> bondValue += snapshot.marketValue
                AssetClass.CASH -> cashValue += snapshot.marketValue
                AssetClass.STOCK, AssetClass.ETF, AssetClass.REIT -> stockValue += snapshot.marketValue
            }
        }

        return PortfolioSummary(
            totalMarketValue = totalMarketValue,
            totalCostBasis = totalCostBasis,
            totalUnrealizedGainLoss = totalUnrealized,
            totalUnrealizedGainLossPercent = totalUnrealizedPercent,
            estimatedAnnualIncome = totalIncome,
            stockAllocationPercent = percentOf(stockValue, totalMarketValue),
            bondAllocationPercent = percentOf(bondValue, totalMarketValue),
            cashAllocationPercent = percentOf(cashValue, totalMarketValue),
            positions = weightedSnapshots.sortedByDescending { it.marketValue },
        )
    }

    private fun buildPositionSnapshot(position: Position, quote: Quote?): PositionSnapshot {
        val price = quote?.price?.takeIf { it > 0 }
        val hasPricedQuote = price != null
        val hasLivePrice = hasPricedQuote && (quote?.isLivePrice != false)
        val shares = position.totalShares()
        val costBasis = position.totalCostBasis()

        val lotSnapshots = position.lots.map { lot ->
            val lotCost = lot.shares * lot.costBasisPerShare
            val lotMarketValue = price?.let { it * lot.shares } ?: lotCost
            val lotUnrealized = if (hasPricedQuote) lotMarketValue - lotCost else 0.0
            val lotUnrealizedPercent = if (lotCost > 0 && hasPricedQuote) {
                (lotUnrealized / lotCost) * 100
            } else 0.0
            LotSnapshot(
                lot = lot,
                marketValue = lotMarketValue,
                costBasis = lotCost,
                unrealizedGainLoss = lotUnrealized,
                unrealizedGainLossPercent = lotUnrealizedPercent,
            )
        }

        val marketValue = price?.let { it * shares } ?: costBasis
        val unrealized = if (hasPricedQuote) marketValue - costBasis else 0.0
        val unrealizedPercent = if (costBasis > 0 && hasPricedQuote) {
            (unrealized / costBasis) * 100
        } else 0.0
        val annualIncome = if (hasPricedQuote) {
            marketValue * (quote?.dividendYield ?: 0.0) / 100.0
        } else 0.0

        return PositionSnapshot(
            position = position,
            quote = quote,
            marketValue = marketValue,
            costBasis = costBasis,
            unrealizedGainLoss = unrealized,
            unrealizedGainLossPercent = unrealizedPercent,
            estimatedAnnualIncome = annualIncome,
            portfolioWeightPercent = 0.0,
            lotSnapshots = lotSnapshots,
            hasLivePrice = hasLivePrice,
        )
    }

    private fun percentOf(part: Double, total: Double): Double =
        if (total > 0) (part / total) * 100 else 0.0
}
